import axios from "axios";
import { useQuery } from "@tanstack/react-query";

// Mirrors ContactActionPolicy::Result (Ruby). Single source of truth for what
// the viewer can do with a profile + the obfuscation-aware display name.
export interface ContactStatus {
  user_id: number;
  display_name: string;
  revealed: boolean;
  privacy_locked: boolean;
  owner_label: string | null;
  subscribed: boolean;
  reveal_quota_remaining: number | null;
}

export interface RevealResult {
  revealed: boolean;
  reason?: string;
  display_name?: string;
  owner_label?: string | null;
}

export const CONTACT_STATUS_KEY = ["contact-status"] as const;

// Base paths (NO `.json` suffix — that suffix isn't routable here). JSON is
// negotiated via the Accept header so respond_to picks the JSON branch.
const ACTION_STATUSES_URL = "/app/contacts/action_statuses";
const REVEAL_URL = "/app/profile_reveals";
const JSON_HEADERS = { Accept: "application/json" } as const;

export const contactStatusApi = {
  fetch: async (userIds: number[]): Promise<Record<string, ContactStatus>> => {
    if (userIds.length === 0) return {};
    const { data } = await axios.get(ACTION_STATUSES_URL, {
      params: { user_ids: userIds },
      headers: JSON_HEADERS,
    });
    return data;
  },

  // POSTs the single, canonical reveal. Returns the structured result on both
  // success and the gated outcomes (402/403/422) so callers can react.
  reveal: async (userId: number): Promise<RevealResult> => {
    try {
      const { data } = await axios.post(
        REVEAL_URL,
        { user_id: userId },
        { headers: JSON_HEADERS },
      );
      return data;
    } catch (e: unknown) {
      const body = (e as { response?: { data?: RevealResult } })?.response?.data;
      return body ?? { revealed: false, reason: "error" };
    }
  },
};

export function useContactStatuses(userIds: number[]) {
  const ids = [...new Set(userIds)].sort((a, b) => a - b);
  return useQuery({
    queryKey: [...CONTACT_STATUS_KEY, ids],
    queryFn: () => contactStatusApi.fetch(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
}
